import React from 'react';

import { Add, Remove } from '@mui/icons-material';
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import { find, isNil } from 'lodash';
import numeral from 'numeral';
import { useFormContext } from 'react-hook-form';
import { NumberTextField, RadioButtonGroup } from '../../../../components';
import { createScopedI18n } from '../../../../i18n/i18n';
import { formatDate } from '../../../../utils/libs';

import type { JobCreatesSchemaType } from './page';

const jobFieldI18n = createScopedI18n('graphql.fields.job');
const jobCreatePageI18n = createScopedI18n('pages.jobs_new');

export type JobDayCardProps = {
  scheduleDate: string;
  bidPrice: JobCreatesSchemaType['bidPrices'][0];
  onUpdateBidPrice?: (value: JobDayCardProps['bidPrice']) => void;
};

const JobDayCard = ({ scheduleDate, bidPrice, onUpdateBidPrice }: JobDayCardProps) => {
  const { watch } = useFormContext<JobCreatesSchemaType>();

  const jobPrices = watch('jobPrices');
  const jobPricesNormal = watch('jobPricesNormal');

  const jobPrice = React.useMemo(() => find(jobPrices, ({ date }) => date === scheduleDate), [jobPrices]);
  const jobPriceNormal = React.useMemo(
    () => find(jobPricesNormal, ({ date }) => date === scheduleDate),
    [jobPricesNormal],
  );

  const minBidAllowed = React.useMemo(() => jobPrice?.minBidAllowed ?? 0, [jobPrice?.minBidAllowed]);
  const bidPriceRange = React.useMemo(
    () => ({
      min: jobPrice?.minTotalPrice ?? null,
      max: jobPrice?.maxTotalPrice ?? null,
    }),
    [jobPrice],
  );
  const bidPriceNormalRange = React.useMemo(
    () => ({
      min: jobPriceNormal?.minTotalPrice ?? null,
      max: jobPriceNormal?.maxTotalPrice ?? null,
    }),
    [jobPriceNormal],
  );
  const bidPriceRangeString = React.useMemo(() => {
    if (isNil(bidPriceNormalRange.min) || isNil(bidPriceNormalRange.max)) {
      return jobCreatePageI18n('dynamic_price');
    }
    if (bidPriceNormalRange.min === bidPriceNormalRange.max) {
      return bidPriceNormalRange.max;
    }
    return `${bidPriceNormalRange.min} - ${bidPriceNormalRange.max}`;
  }, [bidPriceNormalRange.min, bidPriceNormalRange.max]);

  const onUpdateBidPriceHandler = React.useCallback(
    (value: Partial<JobDayCardProps['bidPrice']>) => {
      onUpdateBidPrice?.({ ...bidPrice, ...value });
    },
    [bidPrice, onUpdateBidPrice],
  );

  const customBidPriceHandler = React.useCallback(
    (priceOrFn: number | null | ((prev: number) => number | null)) => {
      const price = typeof priceOrFn === 'function' ? priceOrFn(bidPrice.restaurantBidPricePerHour ?? 0) : priceOrFn;

      if (!isNil(price) && price < minBidAllowed) return;

      onUpdateBidPriceHandler({ restaurantBidPricePerHour: price ?? null });
    },
    [onUpdateBidPriceHandler, minBidAllowed],
  );

  React.useEffect(() => {
    if (bidPrice.isBidPrice) {
      customBidPriceHandler(bidPrice.restaurantBidPricePerHour ?? minBidAllowed);
    } else {
      customBidPriceHandler(null);
    }
  }, [bidPrice.isBidPrice]);

  return (
    <Card>
      <CardHeader title={formatDate(scheduleDate)} />
      <CardContent>
        <Grid container gap={1}>
          <Grid item container alignItems={'center'} gap={1}>
            <Grid item xs={3}>
              <FormLabel>{jobFieldI18n('bid_price.rate')} *</FormLabel>
            </Grid>
            <Grid item xs overflow={'auto'} whiteSpace={'nowrap'}>
              <RadioButtonGroup
                color="primary"
                value={bidPrice.isBidPrice}
                onChangeValue={(value) => onUpdateBidPriceHandler({ isBidPrice: value ?? false })}
                items={[
                  { label: bidPriceRangeString, value: false },
                  { label: jobFieldI18n('bid_price.custom'), value: true },
                ]}
              />
            </Grid>
          </Grid>

          <Collapse in={bidPrice.isBidPrice} sx={{ width: '100%' }}>
            <Grid item container alignItems={'center'} gap={1}>
              <Grid item xs={3} />
              <Grid item container xs alignItems="center" spacing={1}>
                <Grid item>
                  <NumberTextField
                    value={bidPrice.restaurantBidPricePerHour ?? ''}
                    onNumberChange={customBidPriceHandler}
                    InputProps={{
                      inputProps: { pattern: '[0-9]*', sx: { maxWidth: 64, textAlign: 'center' } },
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            disabled={minBidAllowed === bidPrice.restaurantBidPricePerHour}
                            onClick={() => customBidPriceHandler((prev) => prev - 1)}
                          >
                            <Remove />
                          </IconButton>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => customBidPriceHandler((prev) => prev + 1)}>
                            <Add />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item>
                  <Typography variant="h6">
                    {jobFieldI18n('bid_price.paid_total', {
                      total: numeral(bidPriceRange.max).format('0,0.00'),
                    })}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item container gap={1} mt={1}>
              <Grid item xs={3} />
              <Grid item xs>
                <Alert severity="warning">{jobCreatePageI18n('bid_price_warning')}</Alert>
              </Grid>
            </Grid>
          </Collapse>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default JobDayCard;
